import { useEffect, useId, useRef } from 'react';
import './jqueryGlobals'; // window.jQuery before select2 loads
import $ from 'jquery';
// select2's CJS export is an initializer that must be CALLED with (window, jQuery)
// to attach $.fn.select2 — importing it alone does nothing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import select2Factory from 'select2';
(select2Factory as unknown as (w: Window, jq: typeof $) => void)(window, $);

export interface Select2Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Select2Props {
  label?: string;
  value: string | number | null | undefined;
  onChange: (value: string | null) => void;
  options: Select2Option[];
  placeholder?: string;
  searchable?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  error?: string;
  id?: string;
  className?: string;
  // Where select2 renders its dropdown. Pass a container that is NOT inside an
  // overflow-scroll region (e.g. a modal body above the table scroll) so the dropdown
  // isn't clipped and stacks correctly inside a modal. Defaults to inline.
  dropdownParent?: HTMLElement | null;
}

// Wraps the real select2 jQuery plugin (same library the legacy site uses) so it
// renders pixel-perfect with the theme's select2 CSS (noa + select-list.css).
// Controlled via value/onChange; options are React-rendered <option>s and select2
// is re-initialised when they change (e.g. async filter data).
export function Select2({
  label,
  value,
  onChange,
  options,
  placeholder = 'All',
  searchable = true,
  allowClear = true,
  disabled = false,
  error,
  id,
  className,
  dropdownParent,
}: Select2Props) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const selectRef = useRef<HTMLSelectElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const optionsKey = options.map((o) => `${o.value}:${o.label}`).join('|');

  // (Re)initialise select2 when config/options change.
  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const opts: Record<string, unknown> = {
      width: '100%',
      placeholder,
      allowClear,
      minimumResultsForSearch: searchable ? 0 : Infinity,
    };
    if (dropdownParent) opts.dropdownParent = $(dropdownParent);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($el as any).select2(opts);
    // Reflect current value without firing our change handler.
    $el.val(value == null ? '' : String(value)).trigger('change.select2');

    const onSelectChange = () => {
      const v = $el.val();
      onChangeRef.current(v ? String(v) : null);
    };
    $el.on('change', onSelectChange);

    return () => {
      $el.off('change', onSelectChange);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (($el as any).data('select2')) ($el as any).select2('destroy');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey, placeholder, allowClear, searchable, disabled, dropdownParent]);

  // Keep select2 in sync when the value prop changes externally.
  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (($el as any).data('select2')) {
      $el.val(value == null ? '' : String(value)).trigger('change.select2');
    }
  }, [value]);

  return (
    <div className={className || undefined}>
      {label && (
        <label htmlFor={fieldId} className="form-label">
          {label}
        </label>
      )}
      <select
        id={fieldId}
        ref={selectRef}
        className={`form-control${error ? ' is-invalid' : ''}`}
        disabled={disabled}
        defaultValue={value == null ? '' : String(value)}
      >
        <option value="" />
        {options.map((o) => (
          <option key={o.value} value={String(o.value)} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}
